import React, { useEffect, useMemo, useState } from "react";
import {
    Button,
    Dropdown,
    Header,
    Icon,
    Label,
    Loader,
    Message,
    Modal,
    Pagination,
    Table,
} from "semantic-ui-react";
import { toast } from "react-toastify";
import { getRolePerformanceDrilldown } from "../../../services/reporting.service";
import {
    ReportFilters,
    ReportRole,
    RolePerformanceDrilldownResponse,
    RolePerformanceMetric,
} from "../../../types/models/Reporting";
import {
    ORDERS_STATUSES,
    PROSPECT_ORIGIN_LABELS,
    PROSPECTS_STATUSES,
    ROLE_PERFORMANCE_DETAIL_LABELS,
    ROLE_PERFORMANCE_EVENT_LABELS,
} from "../../../utils/constants";
import {
    formatCurrency,
    formatNumber,
    formatReportDate,
    orderStatusColor,
    orderStatusLabel,
} from "../reportingFormatters";

export interface RolePerformanceSelection {
    role: ReportRole;
    userId: string;
    userName: string;
    metric: RolePerformanceMetric;
    label: string;
    displayedValue: string;
}

interface Props {
    selection?: RolePerformanceSelection;
    filters: ReportFilters;
    onClose: () => void;
}

const metricDefinitions: Partial<Record<RolePerformanceMetric, string>> = {
    PROSPECTS: "Prospectos de la cohorte atribuida al colaborador.",
    CONVERTED_PROSPECTS: "Prospectos de la cohorte con venta confirmada al corte.",
    LOST_PROSPECTS: "Prospectos sin venta y con resultado no concretado.",
    OPEN_PROSPECTS: "Prospectos todavía abiertos al corte.",
    CONVERSION_RATE: "Todos los prospectos del denominador; los convertidos se identifican en verde.",
    AVERAGE_DAYS_TO_SALE: "Prospectos convertidos utilizados para calcular el tiempo promedio.",
    ACTIVE_ORDERS: "Pedidos asignados al asesor que continúan activos al corte.",
    ATTENTION_ORDERS: "Pedidos asignados con puntaje operativo de atención de 20 o más.",
    SALES_AMOUNT: "Pedidos creados en el periodo cuyo importe integra el valor mostrado.",
    COLLECTED_AMOUNT: "Pedidos de la cohorte y sus importes cobrados acumulados al corte.",
    ACTIVE_OUTSTANDING_BALANCE: "Pedidos activos cuyo saldo integra el total mostrado.",
    COLLECTION_RATE: "Todos los pedidos utilizados como denominador de cobranza.",
    ASSIGNED_ORDERS: "Pedidos asignados con actividad de instalación en el periodo.",
    COMPLETED_ORDERS: "Pedidos asignados con evento de conclusión en el periodo.",
    INCOMPLETE_ORDERS: "Pedidos asignados con evento inconcluso en el periodo.",
    SCHEDULED_ORDERS: "Pedidos asignados con evento de agenda en el periodo.",
    OVERDUE_INSTALLATIONS: "Pedidos actualmente agendados cuya fecha de instalación ya venció al corte.",
    ORDERS_WITH_GUARANTEE: "Pedidos concluidos en la cohorte que tienen garantía registrada.",
    COMPLETION_RATE: "Todos los pedidos con actividad; los concluidos forman el numerador.",
    GUARANTEE_INCIDENCE: "Pedidos concluidos del denominador; los que tienen garantía forman el numerador.",
    CREATED_ORDERS: "Entradas a borrador registradas por el usuario en el periodo.",
    VALIDATION_TRANSITIONS: "Entradas a validación registradas por el usuario.",
    PRODUCTION_TRANSITIONS: "Entradas a producción registradas por el usuario.",
    PENDING_DRAFT: "Backlog vigente en borrador iniciado por el usuario.",
    PENDING_VALIDATION: "Backlog vigente en validación iniciado por el usuario.",
    DRAFT_TO_VALIDATION_TIME: "Muestras utilizadas para mediana y P90 de borrador a validación.",
    VALIDATION_TO_PRODUCTION_TIME: "Muestras utilizadas para mediana y P90 de validación a producción.",
    EXTERNAL_UPDATES: "Actualizaciones externas creadas por el usuario en el periodo.",
    ORDERS_TOUCHED: "Pedidos distintos con al menos una actualización externa.",
    ACTIVE_ORDERS_TOUCHED: "Pedidos activos con al menos una actualización externa.",
    UPDATES_LAST_30_DAYS: "Actualizaciones dentro de la ventana móvil de 30 días y de los filtros aplicados.",
    LAST_ACTIVITY: "Historial de actualizaciones externas, ordenado desde la más reciente.",
    RECEIVED_TRANSITIONS: "Entradas a almacén registradas por el usuario.",
    READY_TO_SCHEDULE_TRANSITIONS: "Entradas a listo para agendar registradas por el usuario.",
    SCHEDULED_TRANSITIONS: "Entradas a agendado registradas por el usuario.",
    COMPLETED_TRANSITIONS: "Entradas a concluido registradas por el usuario.",
    INCOMPLETE_TRANSITIONS: "Entradas a inconcluso registradas por el usuario.",
    SUCCESSFUL_CLOSURE_RATE: "Cierres concluidos e inconclusos; los concluidos forman el numerador.",
    PENDING_IN_WAREHOUSE: "Backlog vigente en almacén iniciado por el usuario.",
    PENDING_TO_SCHEDULE: "Backlog vigente listo para agendar iniciado por el usuario.",
    WAREHOUSE_TO_READY_TIME: "Muestras de tiempo entre almacén y listo para agendar.",
    READY_TO_SCHEDULED_TIME: "Muestras de tiempo entre listo para agendar y agendado.",
};

const basisLabels: Record<string, string> = {
    PROSPECT_CREATED_AT: "creación del prospecto",
    ORDER_CREATED_AT: "creación del pedido",
    STATUS_TRANSITION_STARTED_AT: "fecha de transición",
    EXTERNAL_UPDATE_CREATED_AT: "fecha de actualización externa",
    CURRENT_BACKLOG_AT_CUTOFF: "estado vigente al corte",
};

const statusLabel = (status?: string) => status
    ? PROSPECTS_STATUSES[status]?.name
        || ORDERS_STATUSES[status]?.name
        || ROLE_PERFORMANCE_EVENT_LABELS[status]
        || status
    : "Sin estado";

const detailLabel = (detail?: string) => detail
    ? PROSPECT_ORIGIN_LABELS[detail]
        || ROLE_PERFORMANCE_DETAIL_LABELS[detail]
        || detail
    : undefined;

const RolePerformanceDrilldownModal = ({ selection, filters, onClose }: Props) => {
    const [data, setData] = useState<RolePerformanceDrilldownResponse>();
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);

    useEffect(() => {
        setPage(0);
        setData(undefined);
    }, [selection?.role, selection?.userId, selection?.metric, filters]);

    useEffect(() => {
        if (!selection) return;
        let mounted = true;
        const load = async () => {
            setLoading(true);
            try {
                const response = await getRolePerformanceDrilldown(
                    selection.role,
                    selection.userId,
                    selection.metric,
                    filters,
                    { page, size }
                );
                if (mounted) setData(response.data);
            } catch (error: any) {
                if (mounted) toast.error(error.message || "No fue posible consultar el detalle de la métrica.");
            } finally {
                if (mounted) setLoading(false);
            }
        };
        load();
        return () => { mounted = false; };
    }, [selection, filters, page, size]);

    const showMoney = useMemo(() => selection && [
        "SALES_AMOUNT",
        "COLLECTED_AMOUNT",
        "ACTIVE_OUTSTANDING_BALANCE",
        "COLLECTION_RATE",
    ].includes(selection.metric), [selection]);
    const showDuration = Boolean(data?.items.some((item) => item.durationHours != null));
    const commercialDuration = data?.recordType === "PROSPECT";
    const isOrderStage = (status?: string) =>
        data?.recordType !== "PROSPECT" && Boolean(status && ORDERS_STATUSES[status]);
    const renderStatus = (status?: string) => isOrderStage(status) ? (
        <Label color={orderStatusColor(status)}>{orderStatusLabel(status)}</Label>
    ) : statusLabel(status);

    return (
        <Modal
            open={Boolean(selection)}
            onClose={onClose}
            size="fullscreen"
            closeIcon
            className="dashboard-fullscreen-modal dashboard-role-drilldown-modal"
        >
            <Modal.Header>
                <Header as="h3">
                    <Icon name="list alternate outline" />
                    <Header.Content>
                        {selection?.label || "Detalle de desempeño"}
                        <Header.Subheader>
                            {selection?.userName} · valor mostrado {selection?.displayedValue}
                        </Header.Subheader>
                    </Header.Content>
                </Header>
            </Modal.Header>
            <Modal.Content scrolling>
                {selection && (
                    <Message
                        info
                        header={metricDefinitions[selection.metric] || "Registros que componen la métrica"}
                        content={`Base de fecha: ${basisLabels[data?.dateBasis || ""] || data?.dateBasis || "consultando"}. Corte: ${formatReportDate(data?.cutoff, true)}.`}
                    />
                )}

                {loading && <Loader active inline="centered">Consultando réplica...</Loader>}
                {!loading && data?.items.length === 0 && (
                    <Message info content="No hay registros para esta métrica con los filtros seleccionados." />
                )}
                {!loading && Boolean(data?.items.length) && (
                    <div className="dashboard-table-scroll">
                        <Table striped compact selectable className="dashboard-table">
                            <Table.Header><Table.Row>
                                <Table.HeaderCell>{data?.recordType === "PROSPECT" ? "Prospecto" : "Pedido"}</Table.HeaderCell>
                                <Table.HeaderCell>Fecha relevante</Table.HeaderCell>
                                <Table.HeaderCell>Estado / evento</Table.HeaderCell>
                                {showDuration && <Table.HeaderCell textAlign="right">Duración</Table.HeaderCell>}
                                {showMoney && <>
                                    <Table.HeaderCell textAlign="right">Total</Table.HeaderCell>
                                    <Table.HeaderCell textAlign="right">Cobrado</Table.HeaderCell>
                                    <Table.HeaderCell textAlign="right">Saldo</Table.HeaderCell>
                                </>}
                                <Table.HeaderCell>Detalle</Table.HeaderCell>
                                <Table.HeaderCell />
                            </Table.Row></Table.Header>
                            <Table.Body>{data?.items.map((item) => (
                                <Table.Row key={item.itemId} positive={item.numerator === true}>
                                    <Table.Cell>
                                        <strong>{item.subjectName || (item.invoice ? `Pedido #${item.invoice}` : "Sin nombre")}</strong>
                                        {data.recordType === "PROSPECT" && (
                                            <div className="dashboard-muted">
                                                {item.invoice ? `Pedido #${item.invoice}` : "Sin pedido enlazado"}
                                            </div>
                                        )}
                                    </Table.Cell>
                                    <Table.Cell>{formatReportDate(item.occurredAt, true)}</Table.Cell>
                                    <Table.Cell>
                                        {item.eventType && (isOrderStage(item.eventType)
                                            ? renderStatus(item.eventType)
                                            : <Label basic>{statusLabel(item.eventType)}</Label>)}
                                        {item.previousStatus && <span className="dashboard-muted"> desde {renderStatus(item.previousStatus)}</span>}
                                        {!item.eventType && renderStatus(item.currentStatus)}
                                    </Table.Cell>
                                    {showDuration && (
                                        <Table.Cell textAlign="right">
                                            {item.durationHours == null
                                                ? "—"
                                                : `${Number(item.durationHours).toFixed(1)} ${commercialDuration ? "días" : "h"}`}
                                        </Table.Cell>
                                    )}
                                    {showMoney && <>
                                        <Table.Cell textAlign="right">{formatCurrency(item.totalAmount)}</Table.Cell>
                                        <Table.Cell textAlign="right">{formatCurrency(item.paidAmount)}</Table.Cell>
                                        <Table.Cell textAlign="right"><strong>{formatCurrency(item.outstandingBalance)}</strong></Table.Cell>
                                    </>}
                                    <Table.Cell>{detailLabel(item.detail) || (item.numerator === true ? "Incluido en numerador" : item.numerator === false ? "Denominador" : "—")}</Table.Cell>
                                    <Table.Cell collapsing>
                                        {item.orderId && (
                                            <Button
                                                as="a"
                                                secondary
                                                icon="external"
                                                size="mini"
                                                aria-label={`Abrir pedido ${item.invoice || item.orderId}`}
                                                href={`/orders/${item.orderId}/details`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            />
                                        )}
                                    </Table.Cell>
                                </Table.Row>
                            ))}</Table.Body>
                        </Table>
                    </div>
                )}

                <div className="dashboard-pagination dashboard-pagination--split">
                    <span>{formatNumber(data?.totalElements)} registros</span>
                    <Pagination
                        activePage={page + 1}
                        totalPages={Math.max(data?.totalPages || 1, 1)}
                        onPageChange={(_, pagination) => setPage(Number(pagination.activePage) - 1)}
                    />
                    <Dropdown
                        compact
                        selection
                        value={size}
                        options={[10, 25, 50].map((value) => ({ key: value, value, text: `${value} filas` }))}
                        onChange={(_, value) => { setPage(0); setSize(Number(value.value)); }}
                    />
                </div>
            </Modal.Content>
            <Modal.Actions>
                <Button onClick={onClose}><Icon name="cancel" /> Cerrar</Button>
            </Modal.Actions>
        </Modal>
    );
};

export default RolePerformanceDrilldownModal;
